import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { Tabs } from '@/components/common/Tabs';
import { useToast } from '@/components/common/toast';
import { nopThresholdApi } from '@/api/services/nopThresholds';
import { NOP_THRESHOLD_TYPE, type NopThresholdDto, type NopThresholdSaveDto } from '@/api/types/nopThresholds';

const VM_ALL = 'ALL';

interface Grants {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function NopThresholdsPanel({ open, onClose, grants }: { open: boolean; onClose: () => void; grants: Grants }) {
  const [tab, setTab] = useState('nop');
  useEffect(() => {
    if (open) setTab('nop');
  }, [open]);

  return (
    <>
      <div className={`offcanvas offcanvas-end nop-thresholds-offcanvas${open ? ' show' : ''}`} tabIndex={-1} style={{ visibility: open ? 'visible' : 'hidden' }}>
        <div className="offcanvas-header border-bottom py-2 px-3">
          <h5 className="offcanvas-title fw-bold mb-0">NOP Thresholds</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        <div className="offcanvas-body p-0">
          <Tabs
            className="pe-3 pt-2"
            variant="underline"
            tabs={[
              { key: 'nop', title: 'NOP Threshold' },
              { key: 'vm', title: 'Volume Matching' },
            ]}
            active={tab}
            onChange={setTab}
          />
          {open && tab === 'nop' && <NopThresholdForm grants={grants} />}
          {open && tab === 'vm' && <VolumeMatchingForm grants={grants} />}
        </div>
      </div>
      {open && <div className="offcanvas-backdrop fade show" onClick={onClose} />}
    </>
  );
}

// ---- NOP Threshold (Net Volume) sub-panel ----

interface NopFormState {
  id: number | null;
  name: string;
  targetTypes: string[];
  symbols: string[];
  v1: string;
  v2: string;
  v3: string;
  sendEmail: boolean;
}
const emptyNop: NopFormState = { id: null, name: '', targetTypes: [], symbols: [], v1: '', v2: '', v3: '', sendEmail: false };

function NopThresholdForm({ grants }: { grants: Grants }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<NopFormState>(emptyNop);

  const targetTypes = useQuery({ queryKey: ['nt', 'target-types'], queryFn: nopThresholdApi.getTargetTypes, staleTime: 5 * 60_000 });
  const symbols = useQuery({ queryKey: ['nt', 'symbols'], queryFn: nopThresholdApi.getSymbols, staleTime: 5 * 60_000 });
  const list = useQuery({ queryKey: ['nt', 'list', NOP_THRESHOLD_TYPE.netVolume], queryFn: () => nopThresholdApi.getList(NOP_THRESHOLD_TYPE.netVolume) });

  const reset = () => setForm(emptyNop);
  const fill = (p: NopThresholdDto) =>
    setForm({
      id: p.id,
      name: p.name ?? '',
      targetTypes: (p.targetTypes ?? []).map(String),
      symbols: p.symbols ?? [],
      v1: p.thresholdValue1 != null ? String(p.thresholdValue1) : '',
      v2: p.thresholdValue2 != null ? String(p.thresholdValue2) : '',
      v3: p.thresholdValue3 != null ? String(p.thresholdValue3) : '',
      sendEmail: !!p.sendEmailNotifications,
    });

  const num = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = parseFloat(v.replace(/,/g, ''));
    return Number.isNaN(n) ? null : Math.round(n * 100) / 100;
  };

  const save = async () => {
    const dto: NopThresholdSaveDto = {
      id: form.id,
      name: form.name.trim(),
      thresholdType: NOP_THRESHOLD_TYPE.netVolume,
      targetTypes: form.targetTypes.map((v) => parseInt(v, 10)).filter(Number.isFinite),
      symbols: form.symbols,
      thresholdValue1: num(form.v1),
      thresholdValue2: num(form.v2),
      thresholdValue3: num(form.v3),
      isActive: true,
      sendEmailNotifications: form.sendEmail,
    };
    if (!dto.name) return toast.error('Please enter a profile name.');
    if (!dto.targetTypes.length) return toast.error('Please select at least one target type.');
    if (!dto.symbols.length) return toast.error('Please select at least one symbol.');
    const vals = [dto.thresholdValue1, dto.thresholdValue2, dto.thresholdValue3].filter((v): v is number => v != null);
    if (!vals.length) return toast.error('Please enter at least one threshold value.');
    if (vals.length > 1) {
      const allNeg = vals.every((v) => v < 0);
      for (let i = 1; i < vals.length; i++) {
        const ordered = allNeg ? Math.abs(vals[i]) > Math.abs(vals[i - 1]) : vals[i] > vals[i - 1];
        if (!ordered) return toast.error('Threshold values must be in strictly increasing order.');
      }
    }
    try {
      await nopThresholdApi.save(dto);
      toast.success(form.id ? 'Profile saved successfully' : 'Profile created successfully');
      reset();
      qc.invalidateQueries({ queryKey: ['nt', 'list', NOP_THRESHOLD_TYPE.netVolume] });
    } catch {
      toast.error('Failed to save profile');
    }
  };

  return (
    <div className="pt-2 px-3">
      <div className="card mb-0 bg-light">
        <div className="card-body p-3 fs-12 text-muted">
          A notification will be triggered whenever the net volume exceeds any of the configured thresholds.
        </div>
      </div>
      <div className="pt-4 pb-2">
        <h6 className="fw-bold mb-3 fs-15">{form.id ? 'Edit' : 'Create'} Alert Profile</h6>
        <div className="mb-2">
          <label className="form-label">Profile Name</label>
          <input className="form-control" placeholder="Enter profile name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <Select2Multi label="Target Type" placeholder="Select target types" searchable value={form.targetTypes} options={targetTypes.data ?? []} onChange={(v) => setForm((f) => ({ ...f, targetTypes: v }))} />
          </div>
          <div className="col-6">
            <Select2Multi label="Symbols" placeholder="Select symbols" searchable value={form.symbols} options={symbols.data ?? []} onChange={(v) => setForm((f) => ({ ...f, symbols: v }))} />
          </div>
        </div>
        <div className="row g-2 mb-3">
          {(['v1', 'v2', 'v3'] as const).map((k, i) => (
            <div className="col-4" key={k}>
              <label className="form-label">Alert {i + 1}</label>
              <input className="form-control" inputMode="decimal" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="nopSendEmail" checked={form.sendEmail} onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))} />
          <label className="form-check-label fs-14" htmlFor="nopSendEmail">Send email notifications</label>
        </div>
        <div className="d-flex justify-content-end gap-2 mb-1 mt-4">
          {form.id != null && <button type="button" className="btn btn-dark px-3" onClick={reset}>Cancel</button>}
          {((form.id == null && grants.canCreate) || (form.id != null && grants.canEdit)) && (
            <button type="button" className="btn btn-primary px-4" onClick={save}>{form.id ? 'Save' : 'Create'}</button>
          )}
        </div>
      </div>
      <ProfilesTable profiles={list.data ?? []} grants={grants} thresholdType={NOP_THRESHOLD_TYPE.netVolume} onEdit={fill} />
    </div>
  );
}

// ---- Volume Matching sub-panel ----

interface VmFormState {
  id: number | null;
  name: string;
  symbols: string[];
  hedgeStatuses: string[];
  sendEmail: boolean;
}
const emptyVm: VmFormState = { id: null, name: '', symbols: [], hedgeStatuses: [], sendEmail: false };

function VolumeMatchingForm({ grants }: { grants: Grants }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<VmFormState>(emptyVm);

  const symbols = useQuery({ queryKey: ['nt', 'vm-symbols'], queryFn: nopThresholdApi.getVolumeMatchingSymbols, staleTime: 5 * 60_000 });
  const hedge = useQuery({ queryKey: ['nt', 'hedge'], queryFn: nopThresholdApi.getHedgeStatuses, staleTime: 5 * 60_000 });
  const list = useQuery({ queryKey: ['nt', 'list', NOP_THRESHOLD_TYPE.volumeMatching], queryFn: () => nopThresholdApi.getList(NOP_THRESHOLD_TYPE.volumeMatching) });

  const reset = () => setForm(emptyVm);
  const fill = (p: NopThresholdDto) =>
    setForm({
      id: p.id,
      name: p.name ?? '',
      symbols: p.applyToAllSymbols ? [VM_ALL] : p.symbols ?? [],
      hedgeStatuses: (p.hedgeStatuses ?? []).map(String),
      sendEmail: !!p.sendEmailNotifications,
    });

  // "ALL" is exclusive: picking ALL clears others; picking another clears ALL.
  const onSymbols = (next: string[]) => {
    setForm((f) => {
      if (next.length > 1 && next.includes(VM_ALL)) {
        const addedAll = !f.symbols.includes(VM_ALL);
        return { ...f, symbols: addedAll ? [VM_ALL] : next.filter((v) => v !== VM_ALL) };
      }
      return { ...f, symbols: next };
    });
  };

  const save = async () => {
    const applyToAll = form.symbols.includes(VM_ALL);
    const dto: NopThresholdSaveDto = {
      id: form.id,
      name: form.name.trim(),
      thresholdType: NOP_THRESHOLD_TYPE.volumeMatching,
      targetTypes: [],
      applyToAllSymbols: applyToAll,
      symbols: applyToAll ? [] : form.symbols,
      hedgeStatuses: form.hedgeStatuses.map((v) => parseInt(v, 10)).filter(Number.isFinite),
      isActive: true,
      sendEmailNotifications: form.sendEmail,
    };
    if (!dto.name) return toast.error('Please enter a profile name.');
    if (!dto.hedgeStatuses!.length) return toast.error('Please select at least one hedge status.');
    if (!applyToAll && !dto.symbols.length) return toast.error('Select at least one symbol or enable "Apply to all symbols".');
    try {
      await nopThresholdApi.save(dto);
      toast.success(form.id ? 'Profile saved successfully' : 'Profile created successfully');
      reset();
      qc.invalidateQueries({ queryKey: ['nt', 'list', NOP_THRESHOLD_TYPE.volumeMatching] });
    } catch {
      toast.error('Failed to save profile');
    }
  };

  const symbolOptions = [{ value: VM_ALL, label: 'ALL' }, ...(symbols.data ?? [])];

  return (
    <div className="pt-2 px-3">
      <div className="card mb-0 bg-light">
        <div className="card-body p-3 fs-12 text-muted">
          A notification will be triggered whenever a symbol matches the selected hedge status conditions.
        </div>
      </div>
      <div className="pt-4 pb-2">
        <h6 className="fw-bold mb-3 fs-15">{form.id ? 'Edit' : 'Create'} Alert Profile</h6>
        <div className="mb-2">
          <label className="form-label">Profile Name</label>
          <input className="form-control" placeholder="Enter profile name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <Select2Multi label="Symbols" placeholder="Select symbols" searchable value={form.symbols} options={symbolOptions} onChange={onSymbols} />
          </div>
          <div className="col-6">
            <Select2Multi label="Hedge Status" placeholder="Select hedge status" searchable value={form.hedgeStatuses} options={hedge.data ?? []} onChange={(v) => setForm((f) => ({ ...f, hedgeStatuses: v }))} />
          </div>
        </div>
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="vmSendEmail" checked={form.sendEmail} onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))} />
          <label className="form-check-label fs-14" htmlFor="vmSendEmail">Send email notifications</label>
        </div>
        <div className="d-flex justify-content-end gap-2 mb-1 mt-4">
          {form.id != null && <button type="button" className="btn btn-dark px-3" onClick={reset}>Cancel</button>}
          {((form.id == null && grants.canCreate) || (form.id != null && grants.canEdit)) && (
            <button type="button" className="btn btn-primary px-4" onClick={save}>{form.id ? 'Save' : 'Create'}</button>
          )}
        </div>
      </div>
      <ProfilesTable profiles={list.data ?? []} grants={grants} thresholdType={NOP_THRESHOLD_TYPE.volumeMatching} onEdit={fill} />
    </div>
  );
}

// ---- Shared profiles table (name + summary, toggle, delete) ----

function ProfilesTable({
  profiles,
  grants,
  thresholdType,
  onEdit,
}: {
  profiles: NopThresholdDto[];
  grants: Grants;
  thresholdType: number;
  onEdit: (p: NopThresholdDto) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: ['nt', 'list', thresholdType] });

  const toggle = async (p: NopThresholdDto, next: boolean) => {
    try {
      await nopThresholdApi.toggle(p.id, next);
      toast.success(next ? 'Profile enabled successfully' : 'Profile disabled successfully');
      refetch();
    } catch {
      toast.error('Failed to toggle profile');
    }
  };
  const remove = async (p: NopThresholdDto) => {
    if (!window.confirm('This will permanently delete this alert profile. Are you sure?')) return;
    try {
      await nopThresholdApi.remove(p.id);
      toast.success('Profile deleted successfully');
      refetch();
    } catch {
      toast.error('Failed to delete profile');
    }
  };

  return (
    <div className="pt-4">
      <h6 className="fw-bold mb-2 fs-15">Alert Profiles</h6>
      <div className="threshold-profiles-table" style={{ maxHeight: '35vh', overflowY: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <tbody>
            {profiles.length === 0 ? (
              <tr>
                <td className="text-center text-muted py-3">No alert profiles</td>
              </tr>
            ) : (
              profiles.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="threshold-profile-info" role={grants.canEdit ? 'button' : undefined} onClick={() => grants.canEdit && onEdit(p)}>
                      <div className="fw-semibold fs-14">{p.name || `Profile #${p.id}`}</div>
                      <div className="text-muted fs-10 text-truncate" title={p.summary ?? ''}>
                        {(p.summary ?? '').length > 40 ? `${(p.summary ?? '').slice(0, 40).trimEnd()}…` : p.summary}
                      </div>
                    </div>
                  </td>
                  <td className="text-end" style={{ width: 90 }}>
                    <div className="form-check form-switch d-inline-block align-middle m-0 me-2">
                      <input type="checkbox" role="switch" className="form-check-input" checked={p.isActive} disabled={!grants.canEdit} onChange={(e) => toggle(p, e.target.checked)} />
                    </div>
                    {grants.canDelete && (
                      <button type="button" className="btn btn-xs btn-outline-danger" title="Delete" onClick={() => remove(p)}>
                        <i className="fa-solid fa-trash-can" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
